import { Moment } from "moment";
import momentTz from "moment-timezone";
import { MarketSchedules } from "../../../openapi-typescript/common/market_schedules";

export const getMarketTimeBoundaries = (markets: MarketSchedules[]) => {
  const localTimes = markets.map((market) => {
    const openTime = getMarketOpenOrCloseTime(market, "openTime");
    const closeTime = getMarketOpenOrCloseTime(market, "closeTime");
    return { openTime, closeTime };
  });

  const earliestOpen = localTimes.reduce(
    (min, t) => (t.openTime.isBefore(min) ? t.openTime : min),
    localTimes[0].openTime
  );

  const latestClose = localTimes.reduce(
    (max, t) => (t.closeTime.isAfter(max) ? t.closeTime : max),
    localTimes[0].closeTime
  );

  return {
    earliestOpen,
    latestClose,
  };
};

export const getMarketOpenOrCloseTime = (
  item: MarketSchedules,
  key: Extract<keyof MarketSchedules, "openTime" | "closeTime">
) => {
  const now = momentTz.tz(item.timezone);
  return momentTz
    .tz(
      `${now.format("YYYY-MM-DD")} ${item[key]}`,
      "YYYY-MM-DD HH:mm",
      item.timezone
    )
    .local();
};

export const checkIsMarketOpen = (item: MarketSchedules) => {
  const now = momentTz();
  const storeDateToday = momentTz.tz(now, item.timezone);
  const todayStr = storeDateToday.format("YYYY-MM-DD");
  const isHoliday = item.calendar?.holidays?.some(
    (holiday) => holiday.date === todayStr
  );
  const dayOfWeek = storeDateToday.isoWeekday();
  // dayOfWeek e.g. monday = 1, Sunday = 7
  if (isHoliday || dayOfWeek === 6 || dayOfWeek === 7) {
    return false;
  }

  const openTime = getMarketOpenOrCloseTime(item, "openTime");
  const closeTime = getMarketOpenOrCloseTime(item, "closeTime");

  return checkIsMarketOpenCondition(now, openTime, closeTime);
};

export const checkIsMarketOpenCondition = (
  now: Moment,
  openTime: Moment,
  closeTime: Moment
) => {
  if (!now?.isValid() || !openTime?.isValid() || !closeTime?.isValid()) {
    return false;
  }

  if (openTime.isSameOrBefore(closeTime)) {
    return now.isBetween(openTime, closeTime, null, "[]");
  } else {
    // Market opens today and closes on the next day (e.g. overnight market 22:00 - 05:00 next day)
    return now.isSameOrAfter(openTime) || now.isSameOrBefore(closeTime);
  }
};

export const getNextMarketOpenTime = (item: MarketSchedules): Moment => {
  let date = momentTz.tz(item.timezone).startOf("day");

  // Look for the next market open time within the next 30 days
  for (let i = 0; i < 30; i++) {
    const dayStr = date.format("YYYY-MM-DD");
    const isHoliday = item.calendar?.holidays?.some((h) => h.date === dayStr);
    const dayOfWeek = date.isoWeekday();

    // dayOfWeek e.g. monday = 1, Sunday = 7
    if (!isHoliday && dayOfWeek < 6) {
      return momentTz
        .tz(`${dayStr} ${item.openTime}`, "YYYY-MM-DD HH:mm", item.timezone)
        .local();
    }

    date = date.add(1, "day");
  }

  return momentTz
    .tz(
      `${date.format("YYYY-MM-DD")} ${item.openTime}`,
      "YYYY-MM-DD HH:mm",
      item.timezone
    )
    .local();
};
